import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authAxios } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, Palette, Sparkles, Star, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SubscriptionPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [isTrial, setIsTrial] = useState(false);
  const [daysRemaining, setDaysRemaining] = useState(0);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [plansRes, subRes] = await Promise.all([
        authAxios.get(`${API}/subscription/plans`),
        authAxios.get(`${API}/subscription/me`)
      ]);
      setPlans(plansRes.data.plans || []);
      setSubscription(subRes.data.subscription);
      setHasSubscription(subRes.data.has_subscription);
      setIsTrial(subRes.data.is_trial || false);
      setDaysRemaining(subRes.data.days_remaining || 0);
    } catch (error) {
      console.error('Error fetching plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (planId) => {
    setCheckoutLoading(planId);
    try {
      const originUrl = window.location.origin;
      const res = await authAxios.post(`${API}/subscription/checkout`, {
        plan_id: planId,
        origin_url: originUrl
      });
      
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
    } finally {
      setCheckoutLoading(null);
    }
  };

  const features = [
    "Full access to Lesson Plan Studio",
    "50-State Standards Hub",
    "Unlimited Photo Albums & Upload",
    "Customizable Calendar",
    "Google Drive Integration",
    "Cross-curricular Standards Linking",
    "Reusable Planning Blocks",
    "Priority Support"
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50 px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-blue-600">
              <Palette className="h-5 w-5 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Create(ED)</span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/')}
            className="border-slate-300 text-slate-700 hover:bg-slate-50 rounded-lg"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-4 py-12">
        {/* Hero */}
        <div className="text-center mb-12">
          <Badge className="rounded-full px-4 py-1 text-sm bg-amber-100 text-amber-700 hover:bg-amber-100 mb-4">
            <Sparkles className="h-3 w-3 mr-1 inline" />
            Subscribe & Create
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mt-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Unlock Your Full Art Teaching Toolkit
          </h1>
          <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto" style={{ fontFamily: 'Figtree, sans-serif' }}>
            Get full access to everything in Create(ED) — lesson planning, standards alignment, photo albums, and more.
          </p>
        </div>

        {/* Trial / Active Subscription Banner */}
        {isTrial && hasSubscription && (
          <div className="mb-8 p-6 bg-blue-50 border border-blue-200 rounded-xl text-center" data-testid="trial-banner">
            <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 rounded-full px-4 py-1 text-sm font-medium mb-3">
              <Sparkles className="h-4 w-4" />
              Free Trial Active
            </div>
            <h3 className="text-xl font-semibold text-blue-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              You have {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} left on your free trial
            </h3>
            <p className="text-blue-700 mt-2 max-w-lg mx-auto" style={{ fontFamily: 'Figtree, sans-serif' }}>
              You have full access to all features during your trial. Subscribe now to continue using Create(ED) after your trial ends.
            </p>
          </div>
        )}

        {hasSubscription && subscription && !isTrial && (
          <div className="mb-8 p-6 bg-emerald-50 border border-emerald-200 rounded-xl text-center" data-testid="active-subscription-banner">
            <CheckCircle2 className="h-8 w-8 text-emerald-600 mx-auto mb-2" />
            <h3 className="text-lg font-semibold text-emerald-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
              You have an active subscription!
            </h3>
            <p className="text-emerald-700 mt-1" style={{ fontFamily: 'Figtree, sans-serif' }}>
              {subscription.plan_name} — Active until {new Date(subscription.expires_at).toLocaleDateString()}
            </p>
          </div>
        )}

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto mb-16">
          {/* Monthly Plan */}
          <Card className="rounded-xl border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-testid="plan-monthly">
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-xl text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>Monthly Plan</CardTitle>
              <CardDescription className="text-slate-500" style={{ fontFamily: 'Figtree, sans-serif' }}>Flexible month-to-month access</CardDescription>
            </CardHeader>
            <CardContent className="text-center space-y-6">
              <div>
                <span className="text-5xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>$5</span>
                <span className="text-slate-500 ml-1" style={{ fontFamily: 'Figtree, sans-serif' }}>/month</span>
              </div>
              <Button
                className="w-full rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium py-3"
                onClick={() => handleSubscribe('monthly')}
                disabled={checkoutLoading === 'monthly' || (hasSubscription && !isTrial)}
                data-testid="subscribe-monthly-button"
              >
                {checkoutLoading === 'monthly' ? 'Redirecting...' : (hasSubscription && !isTrial) ? 'Currently Subscribed' : isTrial ? 'Upgrade to Monthly' : 'Subscribe Monthly'}
              </Button>
            </CardContent>
          </Card>

          {/* Yearly Plan */}
          <Card className="rounded-xl border-2 border-blue-600 shadow-sm hover:shadow-md transition-shadow relative" data-testid="plan-yearly">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <Badge className="bg-blue-600 text-white rounded-full px-4 py-1 text-xs">
                <Star className="h-3 w-3 mr-1 inline" /> Best Value — Save 58%
              </Badge>
            </div>
            <CardHeader className="text-center pb-2 pt-8">
              <CardTitle className="text-xl text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>Yearly Plan</CardTitle>
              <CardDescription className="text-slate-500" style={{ fontFamily: 'Figtree, sans-serif' }}>Best value for the school year</CardDescription>
            </CardHeader>
            <CardContent className="text-center space-y-6">
              <div>
                <span className="text-5xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>$25</span>
                <span className="text-slate-500 ml-1" style={{ fontFamily: 'Figtree, sans-serif' }}>/year</span>
                <p className="text-sm text-blue-600 mt-1 font-medium" style={{ fontFamily: 'Figtree, sans-serif' }}>Just $2.08/month</p>
              </div>
              <Button
                className="w-full rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium py-3"
                onClick={() => handleSubscribe('yearly')}
                disabled={checkoutLoading === 'yearly' || (hasSubscription && !isTrial)}
                data-testid="subscribe-yearly-button"
              >
                {checkoutLoading === 'yearly' ? 'Redirecting...' : (hasSubscription && !isTrial) ? 'Currently Subscribed' : isTrial ? 'Upgrade to Yearly' : 'Subscribe Yearly'}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Features List */}
        <div className="max-w-2xl mx-auto">
          <h3 className="text-xl font-semibold text-center text-slate-800 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Everything included in your subscription
          </h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {features.map((feature) => (
              <div key={feature} className="flex items-center gap-3 p-3 rounded-lg bg-white border border-slate-200">
                <CheckCircle2 className="h-5 w-5 text-blue-600 shrink-0" />
                <span className="text-sm text-slate-700" style={{ fontFamily: 'Figtree, sans-serif' }}>{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
